/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { ScrolledPartCardSkeleton } from '@/features/certona';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import { useSelector } from 'react-redux';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useHomePageContent } from '../api/getHomePageContentV2';
import { siteSections } from '@/utils/siteSections';
import { useHeaderData } from '@/features/header/api/getHeader';
import { CMSModularHandler } from './CMSModularHandler';
import { CMSSeoTags } from '@/features/contentstack/components/CMSSeoTags/CMSSeoTags';
import { type AddCertonaItemObjectType } from '@/features/certona';
import styles from './HomePageTemplate.module.scss';
import { Drawer } from '@/components/Drawer/Drawer';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import dynamic from 'next/dynamic';
import { formatPartTypeId } from '@/utils/formatPartTypeId';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { MINI_CART_TITLE_ID } from '@/features/miniCartFlyout';
import { useReviewCandidates } from '@/features/customReviews/api/getReviewCandidates';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAppData } from '@/hooks/useAppData';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

export const HomePageTemplate = () => {
  const isHomePageCertonaAddToCartEnabled = useCertonaAddToCartFlag();
  const {
    appData: { userAuthenticated },
  } = useAppData();
  const { data: contentStackData, isSuccess: contentStackSuccess } = useHomePageContent();
  const hasSetupAnalytics = useAnalytics();
  const pageViewCall = usePageViewCall();
  const { data: headerData } = useHeaderData();
  const preferredVehicle = usePreferredVehicle();
  const { acesEnabled } = useAcesFlags();
  const lazyMiniCartModalRef = useRef<HTMLDivElement>(null);
  const vehicleId = headerData?.vehicleMap?.catalogVehicleId || '';
  const [addedCertonaItemObject, setAddedCertonaItemObject] =
    useState<AddCertonaItemObjectType | null>(null);
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);
  const [showCertonaMiniCartFlyoutModal, setShowCertonaMiniCartFlyoutModal] =
    useState<boolean>(false);
  const [reviewCandidatesEnabled, setReviewCandidatesEnabled] = useState(false);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const pageViewCalledRef = useRef(false);
  const storeNumber = headerData?.storeNumber;
  const miniCartMap = headerData?.miniCartMap;
  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: certonaPageType.homePage,
      vehicleID: acesEnabled ? preferredVehicle?.partsProVehicleId?.toString() : vehicleId,
      ...(!!certonaRecommendationAddedToCart && {
        parttype: formatPartTypeId(addedCertonaItemObject?.productDetails.partType),
      }),
    },
    [vehicleId, storeNumber],
    true,
    false,
  );
  const reviewsContainer = contentStackData?.homepageBody?.find(
    (modularBlock) => modularBlock.reviews_container?.reviews_container?.[0],
  );
  const reviewsContainerLimit =
    reviewsContainer?.reviews_container?.reviews_container?.[0].product_review_limit ?? 0;

  const { data: reviewCandidatesData, isPending: reviewCandidatesPending } = useReviewCandidates({
    limit: reviewsContainerLimit + 1,
    enabled: false,
  });

  const closeStoreModal = () => {
    if (addedCertonaItemObject) {
      setCertonaRecommendationAddedToCart(false);
      setAddedCertonaItemObject(null);
    }
  };

  const toggleCertonaMiniCartFlyout = () => {
    setShowCertonaMiniCartFlyoutModal(() => !showCertonaMiniCartFlyoutModal);
    closeStoreModal();
  };

  useEffect(() => {
    const reviewsContainerEnabled = sessionStorage.getItem('showHomeReviewCandidates') !== 'false';
    setReviewCandidatesEnabled(reviewsContainerEnabled);
  }, []);

  useEffect(() => {
    // Reset page view flag when vehicle changes to trigger new analytics call
    pageViewCalledRef.current = false;
  }, [vehicleId, preferredVehicle]);

  useEffect(() => {
    if (addedCertonaItemObject) {
      setShowCertonaMiniCartFlyoutModal(true);
    }
  }, [addedCertonaItemObject]);

  useContentStackLivePreview({ enabled: true });

  const reviewCandidatesLength = reviewCandidatesData?.productReviewCandidates.length ?? 0;
  const showReviewsContainer =
    reviewCandidatesEnabled &&
    !!reviewsContainer?.reviews_container?.reviews_container?.[0] &&
    !!reviewCandidatesData &&
    reviewCandidatesLength > 0;

  useEffect(() => {
    const shouldWaitForReviewCandidates =
      userAuthenticated && reviewCandidatesEnabled && reviewsContainerLimit > 0;
    const isReadyToCallAnalytics = shouldWaitForReviewCandidates ? !reviewCandidatesPending : true;

    if (
      contentStackSuccess &&
      hasSetupAnalytics &&
      !pageViewCalledRef.current &&
      isReadyToCallAnalytics
    ) {
      const userId = headerData?.myAccountMap?.customerID;

      const pageSpecificAnalyticsData = {
        pageName: 'az:home',
        siteSection: siteSections.siteSectionHome,
        pageType: pageTypes.home,
        pageHierarchy: 'Home',
        seoPageType: 'HOME',
        reviewsContainerDisplayed: showReviewsContainer,
        ...(showReviewsContainer && {
          displayedReviewProducts:
            reviewCandidatesLength <= reviewsContainerLimit
              ? reviewCandidatesLength
              : reviewsContainerLimit,
        }),
        ...(userId && { GCMID: userId }),
      };
      pageViewCall(
        pageSpecificAnalyticsData,
        getAnalyticsRelativeComplementOf(pageSpecificAnalyticsData, undefined),
      );
      pageViewCalledRef.current = true;
    }
  }, [
    contentStackSuccess,
    contentStackData,
    hasSetupAnalytics,
    pageViewCall,
    reviewsContainer,
    reviewCandidatesData,
    reviewsContainerLimit,
    reviewCandidatesPending,
    userAuthenticated,
    showReviewsContainer,
    reviewCandidatesLength,
    reviewCandidatesEnabled,
    vehicleId,
    preferredVehicle,
    headerData?.myAccountMap?.customerID,
  ]);

  return (
    <>
      {contentStackSuccess && contentStackData.homepageBody ? (
        <>
          <CMSModularHandler
            content={contentStackData.homepageBody}
            certonaRecommendations={certonaRecommendations}
            contentSeo={contentStackData.seoTags}
            setAddedCertonaItemObject={setAddedCertonaItemObject}
            setCertonaRecommendationAddedToCart={setCertonaRecommendationAddedToCart}
            onlyShowAvailableProductRecommendations={isHomePageCertonaAddToCartEnabled}
            LoadingSkeleton={ScrolledPartCardSkeleton}
          />
          <div className={styles.miniCartPopup}>
            <Drawer
              data-testid="mini-cart-flyout"
              anchor={'right'}
              open={showCertonaMiniCartFlyoutModal}
              onClose={toggleCertonaMiniCartFlyout}
              PaperProps={{ className: styles.customPaperDrawer }}
              drawerRef={lazyMiniCartModalRef}
            >
              <TrapFocus open={showCertonaMiniCartFlyoutModal}>
                <div
                  className={styles.miniCartFlyoutDialog}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={MINI_CART_TITLE_ID}
                >
                  <Suspense>
                    {showCertonaMiniCartFlyoutModal && addedCertonaItemObject && (
                      <LazyMiniCartFlyoutComp
                        handleClose={toggleCertonaMiniCartFlyout}
                        isModalOpen={showCertonaMiniCartFlyoutModal}
                        image={addedCertonaItemObject.productImage}
                        successData={addedCertonaItemObject.cartSuccessData}
                        productDetails={{
                          productName: addedCertonaItemObject.productDetails.productName,
                          certonaItemAddedTocart:
                            certonaRecommendationAddedToCart && isHomePageCertonaAddToCartEnabled,
                          productPartType: addedCertonaItemObject.productDetails.partType,
                          originalPartTypeId: addedCertonaItemObject.productDetails.partType,
                          productId: addedCertonaItemObject.productDetails.productId,
                          productAvailabilityInfo: {
                            aZBadgesFlagVO: null,
                            dealsInfo: undefined,
                            skuPricingAndAvailability: undefined,
                          },
                        }}
                        miniCartData={miniCartMap}
                        closeStoreModal={closeStoreModal}
                        cartUrl={addedCertonaItemObject.submitToCart}
                        productTitle={addedCertonaItemObject.productTitle}
                        pageName={addedCertonaItemObject.pageName}
                        orderDetailsFromState={orderDetailsFromState}
                        skuId={addedCertonaItemObject.productDetails.skuId}
                        parentRef={lazyMiniCartModalRef}
                        pageLevelCertonaRecommendations={certonaRecommendations}
                      />
                    )}
                  </Suspense>
                </div>
              </TrapFocus>
            </Drawer>
          </div>
        </>
      ) : null}

      {contentStackSuccess && contentStackData.seoTags && (
        <CMSSeoTags seoData={contentStackData.seoTags} />
      )}
    </>
  );
};
